import { Shell } from "@/components/layout/Shell";
import { QuizFlow } from "@/components/quiz/QuizFlow";
import { generatePersonalityRounds, analyzePersonality, Round } from "@/data/quiz";
import { useSeo } from "@/hooks/use-seo";
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { IdentityCard } from "@/components/social/IdentityCard";
import { COLORS, ColorTrait } from "@/data/colors";
import { readInviteFromLocation, computeSynergy, InvitePayload, buildSynergyShareUrl } from "@/lib/compatibility";
import { Users } from "lucide-react";
import { classifyAuraFamily, AURA_READS } from "@/lib/aura-extract";

/** Build 3 short trait bullets from the (1-2) dominant colors' keyword pools. */
function deriveTraits(colors: ColorTrait[]): string[] {
  const [primary, secondary] = [colors[0], colors[1] ?? colors[0]];
  if (!primary) return [];
  return [
    `${primary.keywords[0]}-driven`,
    `${primary.keywords[1] ?? primary.keywords[0]} instincts`,
    `Leans on ${secondary.keywords[0].toLowerCase()}`,
  ];
}

export default function QuizPersonality() {
  useSeo({
    title: "Personality Color Quiz | Mychromamind",
    description:
      "A 7-round instinctive color quiz that reveals your current emotional baseline and personality traits, grounded in color psychology.",
    path: "/quiz/personality",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Personality Color Quiz",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Any (Web)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "A 7-round instinctive color quiz that reveals your current emotional baseline and personality traits, grounded in color psychology.",
      url: "https://mychromamind.com/quiz/personality",
    },
  });

  const [invite] = useState<InvitePayload | null>(() => readInviteFromLocation());
  const [synergyCopied, setSynergyCopied] = useState(false);
  const [rounds, setRounds] = useState<Round[]>(() => generatePersonalityRounds());
  const [isComplete, setIsComplete] = useState(false);
  const [result, setResult] = useState<ReturnType<typeof analyzePersonality> | null>(null);

  const handleComplete = (choices: string[]) => {
    const analysis = analyzePersonality(rounds, choices);
    setResult(analysis);
    setIsComplete(true);
  };

  const synergy =
    result && invite
      ? computeSynergy(
          result.dominantColors,
          invite.colorIds.map((id) => COLORS[id]).filter((c): c is ColorTrait => Boolean(c)),
          result.title,
          invite.title
        )
      : null;

  const handleRetake = () => {
    setRounds(generatePersonalityRounds());
    setIsComplete(false);
    setResult(null);
  };

  return (
    <Shell>
      {!isComplete ? (
        // Quiz entry — visually-hidden h1 ensures one h1 per page for SEO.
        <>
          <h1 className="sr-only">Personality Color Quiz</h1>
          {invite && (
            <p className="text-center text-sm text-muted-foreground px-6 pt-6 -mb-6">
              <span className="font-medium text-foreground">{invite.title}</span> invited you to compare
              Color Synergy — take the quiz to see your match.
            </p>
          )}
          <QuizFlow rounds={rounds} onComplete={handleComplete} />
        </>
      ) : (
        // Result screen — the animated result.title is the visible h1.
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:py-24 max-w-3xl mx-auto w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="w-full"
          >
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className="text-4xl md:text-6xl font-serif mb-8"
            >
              {result?.title}
            </motion.h1>

            {/* Color image — the photo created for each named color */}
            {result?.primaryColorId && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="mx-auto mb-8 w-56 md:w-72"
              >
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={`/og/${result.primaryColorId}.jpg`}
                    alt={result.dominantColors[0]?.name ?? result.primaryColorId}
                    className="w-full h-auto block"
                  />
                </div>
                {result.dominantColors[0] && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <span
                      className="inline-block w-4 h-4 rounded-full border border-border/40 flex-shrink-0"
                      style={{ background: result.dominantColors[0].hex }}
                    />
                    <span className="text-sm font-medium">{result.dominantColors[0].name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{result.dominantColors[0].hex}</span>
                  </div>
                )}
              </motion.div>
            )}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 1 }}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10"
            >
              {result?.description}
            </motion.p>

            {/* Aura signature card */}
            {result?.dominantColors[0] && (() => {
              const auraFamily = classifyAuraFamily(result.dominantColors[0].hex);
              const aura = AURA_READS[auraFamily];
              const excerpt = aura.description.split(". ").slice(0, 2).join(". ") + ".";
              return (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.2, duration: 0.8 }}
                  className="mb-12 max-w-lg mx-auto rounded-2xl border border-border bg-card/70 p-5 text-left"
                >
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                    Aura Signature
                  </p>
                  <p className="text-lg font-serif mb-2">{aura.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{excerpt}</p>
                  <Link
                    href="/aura-scanner"
                    className="mt-3 inline-block text-xs text-primary hover:underline underline-offset-4"
                  >
                    Scan a photo for your full aura read →
                  </Link>
                </motion.div>
              );
            })()}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5, duration: 1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={handleRetake}
                className="px-6 py-3 rounded-full border border-border hover:bg-secondary/5 transition-colors font-medium text-sm"
              >
                Retake Quiz
              </button>
              <Link href="/quiz/decision" className="px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm">
                Explore a Decision
              </Link>
            </motion.div>

            {synergy && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
                className="mb-10 max-w-md mx-auto bg-muted/60 border border-border/60 rounded-2xl p-5 text-left"
              >
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-medium text-foreground">Color Synergy</h2>
                  <span className="text-sm font-semibold text-primary">{synergy.score}%</span>
                </div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{synergy.label}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{synergy.description}</p>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.8, duration: 1 }}
              className="mt-2"
            >
              {result && (
                <IdentityCard
                  title={result.title}
                  colors={result.dominantColors}
                  traits={deriveTraits(result.dominantColors)}
                />
              )}

              {result && result.dominantColors[0] && (
                <div className="mt-6 flex justify-center">
                  <a
                    href={buildSynergyShareUrl(result.dominantColors[0].hex)}
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        await navigator.clipboard.writeText(buildSynergyShareUrl(result.dominantColors[0].hex));
                        setSynergyCopied(true);
                        setTimeout(() => setSynergyCopied(false), 2000);
                      } catch {
                        // Clipboard unavailable — link is still valid to open directly.
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    {synergyCopied ? "Synergy link copied!" : "Copy my Color Synergy link"}
                  </a>
                </div>
              )}

              <p className="mt-8 text-xs text-muted-foreground max-w-md mx-auto">
                For fun and reflection only — not psychological advice.
              </p>
            </motion.div>
          </motion.div>
        </div>
      )}
    </Shell>
  );
}
